import type { DesignLead, ListTeamDTO } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import { Link } from "@/components/link";
import {
  type PaginationMetadata,
  DraggableTable as Table,
} from "@/components/table";
import { useTableSorting } from "@/hooks/useTableSorting";
import { dateFormatter } from "@/utils/dateFormatter";

import { COLUMN_WIDTHS } from "../shared/columnWidths";

interface TeamsTableProps {
  isLoading?: boolean;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
  teams: ListTeamDTO[];
}

const NameCell = memo(function NameCell({
  id,
  name,
}: {
  id: number;
  name: string;
}) {
  const displayName = name || "Untitled";

  return (
    <Link className="hover:no-underline" href={`/teams/${id}/edit`}>
      <div className="flex items-center py-8 min-w-0 w-full overflow-hidden">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
            {displayName}
          </p>
        </div>
      </div>
    </Link>
  );
});

const DesignLeadCell = memo(function DesignLeadCell({
  designLead,
}: {
  designLead: DesignLead | null;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      {designLead ? (
        <Link className="space-y-2" href={`/users/${designLead.id}/edit`}>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-text-tertiary leading-tight line-clamp-2">
                {designLead.name}{" "}
                {designLead.role ? `(${designLead.role})` : ""}
              </p>
            </div>
          </div>
        </Link>
      ) : (
        <span className="text-text-tertiary text-sm">
          No design lead assigned
        </span>
      )}
    </div>
  );
});

const ActiveMembersCell = memo(function ActiveMembersCell({
  count,
}: {
  count: number;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">{count}</span>
    </div>
  );
});

const AssignedAccountsCell = memo(function AssignedAccountsCell({
  count,
}: {
  count: number;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">{count}</span>
    </div>
  );
});

const CreatedAtCell = memo(function CreatedAtCell({
  createdAt,
}: {
  createdAt: string;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">
        {dateFormatter(createdAt)}
      </span>
    </div>
  );
});

export const TeamsTable = memo(function TeamsTable({
  isLoading = false,
  pagination,
  setSortDescriptor,
  sortDescriptor,
  teams,
}: TeamsTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);

  const { handleSort, sorting } = useTableSorting({
    defaultColumn: "createdAt",
    defaultDirection: "descending",
    setSortDescriptor,
    sortDescriptor,
  });

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current.querySelector("table");

      if (table) {
        table.style.tableLayout = "fixed";
        table.style.width = "100%";
      }

      const cells = tableRef.current.querySelectorAll("td");

      for (const cell of cells) {
        cell.style.overflow = "hidden";
        cell.style.maxWidth = "0";
      }
    }
  }, [teams]);

  const columns: ColumnDef<ListTeamDTO>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        cell: ({ getValue, row }) => (
          <NameCell id={row.original.id} name={getValue() as string} />
        ),
        enableSorting: true,
        header: "Name",
        maxSize: COLUMN_WIDTHS.NAME,
        minSize: COLUMN_WIDTHS.NAME,
        size: COLUMN_WIDTHS.NAME,
      },
      {
        accessorKey: "designLead",
        cell: ({ row }) => (
          <DesignLeadCell designLead={row.original.designLead} />
        ),
        enableMultiRemove: false,
        enableSorting: true,
        header: "Design Lead",
        maxSize: COLUMN_WIDTHS.DESIGN_LEAD,
        minSize: COLUMN_WIDTHS.DESIGN_LEAD,
        size: COLUMN_WIDTHS.DESIGN_LEAD,
      },
      {
        accessorKey: "activeTeamMembers",
        cell: ({ getValue }) => (
          <ActiveMembersCell count={getValue() as number} />
        ),
        enableMultiRemove: false,
        enableSorting: true,
        header: "Team Members",
        maxSize: COLUMN_WIDTHS.TEAM_MEMBERS,
        minSize: COLUMN_WIDTHS.TEAM_MEMBERS,
        size: COLUMN_WIDTHS.TEAM_MEMBERS,
      },
      {
        accessorKey: "assignedAccounts",
        cell: ({ getValue }) => (
          <AssignedAccountsCell count={getValue() as number} />
        ),
        enableMultiRemove: false,
        enableSorting: true,
        header: "Assigned Accounts",
        maxSize: COLUMN_WIDTHS.ASSIGNED_ACCOUNTS,
        minSize: COLUMN_WIDTHS.ASSIGNED_ACCOUNTS,
        size: COLUMN_WIDTHS.ASSIGNED_ACCOUNTS,
      },
      {
        accessorKey: "createdAt",
        cell: ({ getValue }) => (
          <CreatedAtCell createdAt={getValue() as string} />
        ),
        enableMultiRemove: false,
        enableSorting: true,
        header: "Created",
        maxSize: COLUMN_WIDTHS.CREATED_DATE,
        minSize: COLUMN_WIDTHS.CREATED_DATE,
        size: COLUMN_WIDTHS.CREATED_DATE,
      },
    ],
    [],
  );

  const getRowId = useCallback((team: ListTeamDTO) => team.id.toString(), []);

  const paginationConfig = useMemo(
    () =>
      pagination
        ? {
            metadata: pagination.metadata,
            onItemsPerPageChange: pagination.onItemsPerPageChange,
            onPageChange: pagination.onPageChange,
            pageSizeOptions: pagination.pageSizeOptions,
            showPageSizeSelector: pagination.showPageSizeSelector,
          }
        : undefined,
    [
      pagination?.metadata,
      pagination?.onItemsPerPageChange,
      pagination?.onPageChange,
      pagination?.pageSizeOptions,
      pagination?.showPageSizeSelector,
    ],
  );

  const sortableColumns = useMemo(
    () => [
      "name",
      "designLead",
      "activeTeamMembers",
      "assignedAccounts",
      "createdAt",
    ],
    [],
  );

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto" ref={tableRef}>
      <Table
        className="rounded-b-xl teams-table w-full"
        columns={columns}
        data={teams}
        getRowId={getRowId}
        isLoading={isLoading}
        onSort={handleSort}
        pagination={paginationConfig}
        sortableColumns={sortableColumns}
        sorting={sorting}
      />
    </div>
  );
});
